import { Field, TextInput, TextArea } from './fields.jsx';
import { CustomerPasswordField } from './CustomerPasswordField.jsx';
import { useCustomerEditForm } from './useCustomerEditForm.js';

/**
 * Modal para o lojista editar os dados de um cliente (incluindo senha).
 * @param {{
 *   tenantId: number|string,
 *   customer: object|null,
 *   onClose: () => void,
 *   onSaved: (customer: object) => void,
 * }} props
 */
export function CustomerEditModal({ tenantId, customer, onClose, onSaved }) {
  const { form, setField, busy, error, submit } = useCustomerEditForm({ tenantId, customer, onSaved });

  if (!customer) return null;

  async function handleSubmit(ev) {
    ev.preventDefault();
    await submit();
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-zinc-900">
        <div className="flex items-center justify-between border-b border-ink/10 px-6 py-4 dark:border-white/10">
          <h3 className="font-display text-lg font-semibold">Editar cliente</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-ink/50 hover:text-ink dark:text-frost-muted dark:hover:text-white"
          >
            Fechar
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 space-y-4 overflow-y-auto overflow-x-hidden px-6 py-5">
          <Field label="Nome">
            <TextInput value={form.nome} onChange={(v) => setField('nome', v)} autoFocus />
          </Field>

          <Field label="E-mail">
            <TextInput
              type="email"
              value={form.email}
              onChange={(v) => setField('email', v)}
              placeholder="cliente@email.com"
            />
          </Field>

          <Field label="Telefone / WhatsApp">
            <div className="flex gap-2">
              <div className="w-16 shrink-0">
                <TextInput
                  className="px-2 text-center"
                  value={form.whatsapp_country_code}
                  onChange={(v) => setField('whatsapp_country_code', v)}
                  placeholder="+55"
                />
              </div>
              <div className="min-w-0 flex-1">
                <TextInput
                  value={form.telefone}
                  onChange={(v) => setField('telefone', v)}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>
          </Field>

          <Field label="Endereço">
            <TextArea
              value={form.endereco}
              onChange={(v) => setField('endereco', v)}
              placeholder="Rua, número, bairro, cidade..."
            />
          </Field>

          <CustomerPasswordField value={form.senha} onChange={(v) => setField('senha', v)} />

          {error ? (
            <p className="rounded-xl bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-300">{error}</p>
          ) : null}

          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>

        <div className="flex items-center justify-end gap-2 border-t border-ink/10 px-6 py-4 dark:border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl px-4 py-2 text-sm font-medium text-ink/60 hover:bg-ink/5 dark:text-frost-muted dark:hover:bg-white/10"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy}
            className="rounded-2xl bg-accent px-5 py-2 text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-50"
          >
            {busy ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
